<template>
  <b-card>
    <b-card-title>{{ $t('Preferences') }}</b-card-title>
    <b-card-sub-title class="mb-2">
      {{ $t('Customize your display and language preferences') }}
    </b-card-sub-title>

    <b-form>
      <!-- Language Section -->
      <div class="preference-section mb-3">
        <h5 class="mb-1">
          <feather-icon
            icon="GlobeIcon"
            size="18"
            class="mr-50"
          />
          {{ $t('Language') }}
        </h5>
        <b-form-group
          :label="$t('Preferred Language')"
          label-for="language-select"
        >
          <v-select
            id="language-select"
            v-model="selectedLocale"
            :options="locales"
            :clearable="false"
            label="name"
            :reduce="option => option.locale"
            class="preference-select"
            @input="changeLocale"
          >
            <template #option="{ name, img }">
              <b-img
                :src="img"
                height="14px"
                width="22px"
                class="mr-50"
              />
              <span>{{ name }}</span>
            </template>
            <template #selected-option="{ name, img }">
              <b-img
                :src="img"
                height="14px"
                width="22px"
                class="mr-50"
              />
              <span>{{ name }}</span>
            </template>
          </v-select>
        </b-form-group>
      </div>

      <hr class="my-2">

      <!-- Navigation & Sidebar Section -->
      <div class="preference-section mb-3">
        <h5 class="mb-1">
          <feather-icon
            icon="SidebarIcon"
            size="18"
            class="mr-50"
          />
          {{ $t('Navigation & Sidebar') }}
        </h5>

        <!-- Sidebar Collapsed -->
        <b-form-group>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="font-weight-bold">{{ $t('Sidebar Collapsed by Default') }}</span>
              <p class="text-muted mb-0 small">
                {{ $t('Start with a minimized sidebar for more workspace') }}
              </p>
            </div>
            <b-form-checkbox
              v-model="isVerticalMenuCollapsed"
              name="sidebar-collapsed"
              switch
              @change="saveSidebarPreference"
            />
          </div>
        </b-form-group>

        <!-- Default Landing Page -->
        <b-form-group
          :label="$t('Default Landing Page')"
          label-for="landing-page"
        >
          <p class="text-muted mb-1 small">
            {{ $t('Choose which page opens after login') }}
          </p>
          <v-select
            id="landing-page"
            v-model="defaultLandingPage"
            :options="landingPageOptions"
            :clearable="false"
            label="label"
            :reduce="option => option.value"
            class="preference-select"
            @input="saveLandingPagePreference"
          />
        </b-form-group>
      </div>

      <hr class="my-2">

      <!-- Typography Section -->
      <div class="preference-section mb-3">
        <h5 class="mb-1">
          <feather-icon
            icon="TypeIcon"
            size="18"
            class="mr-50"
          />
          {{ $t('Typography') }}
        </h5>

        <!-- Preferred Font -->
        <b-form-group
          :label="$t('Preferred Font')"
          label-for="font-select"
        >
          <v-select
            id="font-select"
            v-model="selectedFont"
            :options="preferredFonts"
            :clearable="false"
            label="label"
            :reduce="option => option.value"
            class="preference-select"
            @input="changeFont"
          >
            <template #option="option">
              <span :style="getFontStyle(option.value)">{{ option.label }}</span>
            </template>
            <template #selected-option="option">
              <span :style="getFontStyle(option.value)">{{ option.label }}</span>
            </template>
          </v-select>
        </b-form-group>

        <!-- Font Scaling -->
        <b-form-group
          :label="$t('Font Size')"
        >
          <p class="text-muted mb-1 small">
            {{ $t('Adjust text size for better readability') }}
          </p>
          <div class="font-scale-control">
            <span class="scale-label small">A</span>
            <b-form-input
              v-model="fontScale"
              type="range"
              min="80"
              max="120"
              step="5"
              class="font-scale-slider"
              @input="changeFontScale"
            />
            <span class="scale-label large">A</span>
            <span class="scale-value ml-2">{{ fontScale }}%</span>
          </div>
          <b-button
            v-if="fontScale !== 100"
            variant="outline-secondary"
            size="sm"
            class="mt-1"
            @click="resetFontScale"
          >
            {{ $t('Reset to Default') }}
          </b-button>
        </b-form-group>
      </div>

      <hr class="my-2">

      <!-- Theme Section -->
      <div class="preference-section mb-3">
        <h5 class="mb-1">
          <feather-icon
            icon="SunIcon"
            size="18"
            class="mr-50"
          />
          {{ $t('Theme') }}
        </h5>

        <!-- Skin -->
        <b-form-group :label="$t('Skin')">
          <b-form-radio-group
            id="skin-radio-group"
            v-model="skin"
            name="skin"
            :options="skinOptions"
          />
        </b-form-group>

        <!-- Content Width -->
        <b-form-group :label="$t('Content Width')">
          <b-form-radio-group
            id="content-width-radio-group"
            v-model="contentWidth"
            name="content-width"
            :options="contentWidthOptions"
          />
        </b-form-group>
      </div>

      <hr class="my-2">

      <!-- Animation Section -->
      <div class="preference-section">
        <h5 class="mb-1">
          <feather-icon
            icon="ZapIcon"
            size="18"
            class="mr-50"
          />
          {{ $t('Animation') }}
        </h5>

        <b-form-group
          :label="$t('Router Transition')"
          label-for="router-transition"
        >
          <v-select
            id="router-transition"
            v-model="routerTransition"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :clearable="false"
            label="title"
            :options="routerTransitionOptions"
            :reduce="option => option.value"
            class="preference-select"
          />
        </b-form-group>
      </div>

      <hr class="my-2">

      <!-- Udara AI Section -->
      <div class="preference-section">
        <h5 class="mb-1">
          <feather-icon
            icon="MessageCircleIcon"
            size="18"
            class="mr-50"
          />
          {{ $t('Udara AI') }}
        </h5>

        <b-form-group :label="$t('Chat Position')">
          <p class="text-muted mb-1 small">
            {{ $t('Choose how the AI chat appears on your screen') }}
          </p>
          <b-form-radio-group
            id="chat-position-radio-group"
            v-model="chatPosition"
            name="chat-position"
            :options="chatPositionOptions"
            @change="saveChatPositionPreference"
          />
        </b-form-group>
      </div>
    </b-form>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BCardSubTitle,
  BForm,
  BFormGroup,
  BFormRadioGroup,
  BFormCheckbox,
  BFormInput,
  BButton,
  BImg,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapGetters } from 'vuex'
import useAppCustomizer from '@core/layouts/components/app-customizer/useAppCustomizer'
import useAppConfig from '@core/app-config/useAppConfig'
import { preferredFonts } from '@/helpers/data'
import navMenuItems from '@/navigation/vertical'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardSubTitle,
    BForm,
    BFormGroup,
    BFormRadioGroup,
    BFormCheckbox,
    BFormInput,
    BButton,
    BImg,
    vSelect,
  },
  data() {
    return {
      selectedLocale: this.$i18n.locale,
      selectedFont: localStorage.getItem('preferredFont') || 'Bricolage Grotesque',
      fontScale: parseInt(localStorage.getItem('fontScale'), 10) || 100,
      defaultLandingPage: localStorage.getItem('defaultLandingPage') || 'home',
      chatPosition: localStorage.getItem('chatPosition') || 'floating',
      chatPositionOptions: [
        { text: 'Docked (Bottom Bar)', value: 'docked' },
        { text: 'Floating (Bubble)', value: 'floating' },
      ],
      preferredFonts,
      /* eslint-disable global-require */
      locales: [
        {
          locale: 'en',
          img: require('@/assets/images/flags/en.png'),
          name: 'English',
        },
        {
          locale: 'fr',
          img: require('@/assets/images/flags/fr.png'),
          name: 'French',
        },
      ],
      /* eslint-enable global-require */
    }
  },
  computed: {
    ...mapGetters({
      userData: 'auth/getCurrentUser',
    }),
    landingPageOptions() {
      const options = [{ label: this.$t('Home'), value: 'home' }]
      if (!this.userData || !this.userData.permissions) return options

      const extractItems = items => {
        items.forEach(item => {
          if (item.header) return
          if (item.route && item.enableFeature && item.hasPermission && this.userData.permissions.includes(item.hasPermission)) {
            options.push({ label: this.$t(item.title), value: item.route })
          }
          if (Array.isArray(item.children)) {
            extractItems(item.children)
          }
        })
      }
      extractItems(navMenuItems)
      return options
    },
  },
  setup() {
    const {
      skin,
      skinOptions,
      contentWidth,
      contentWidthOptions,
      routerTransition,
      routerTransitionOptions,
    } = useAppCustomizer()

    const {
      isVerticalMenuCollapsed,
    } = useAppConfig()

    return {
      skin,
      skinOptions,
      contentWidth,
      contentWidthOptions,
      routerTransition,
      routerTransitionOptions,
      isVerticalMenuCollapsed,
    }
  },
  mounted() {
    // Apply saved font scale on mount
    this.applyFontScale(this.fontScale)

    // Load sidebar preference
    const savedSidebarState = localStorage.getItem('sidebarCollapsed')
    if (savedSidebarState !== null) {
      this.isVerticalMenuCollapsed = savedSidebarState === 'true'
    }
  },
  methods: {
    changeLocale(locale) {
      this.$i18n.locale = locale
      localStorage.setItem('language', locale)
    },
    changeFont(font) {
      localStorage.setItem('preferredFont', font)
      document.documentElement.style.setProperty(
        '--font-family-primary',
        `'${font}', sans-serif`,
        'important',
      )
    },
    getFontStyle(fontValue) {
      return {
        fontFamily: `'${fontValue}', sans-serif`,
      }
    },
    changeFontScale(scale) {
      this.fontScale = parseInt(scale, 10)
      localStorage.setItem('fontScale', this.fontScale)
      this.applyFontScale(this.fontScale)
    },
    applyFontScale(scale) {
      const baseSize = 16 * (scale / 100)
      document.documentElement.style.setProperty(
        '--font-size-base',
        `${baseSize}px`,
        'important',
      )
      // Also apply to html for rem-based sizing
      document.documentElement.style.fontSize = `${baseSize}px`
    },
    resetFontScale() {
      this.fontScale = 100
      localStorage.setItem('fontScale', 100)
      this.applyFontScale(100)
    },
    saveSidebarPreference(value) {
      localStorage.setItem('sidebarCollapsed', value)
    },
    saveLandingPagePreference(value) {
      localStorage.setItem('defaultLandingPage', value)
    },
    saveChatPositionPreference(value) {
      localStorage.setItem('chatPosition', value)
      window.dispatchEvent(new CustomEvent('chatPositionChanged', { detail: value }))
    },
  },
}
</script>

<style lang="scss" scoped>
.preference-section {
  h5 {
    display: flex;
    align-items: center;
    font-weight: 600;
  }
}

.preference-select {
  max-width: 300px;
}

.font-scale-control {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 300px;

  .scale-label {
    color: #666;
    font-weight: 500;

    &.small {
      font-size: 12px;
    }

    &.large {
      font-size: 18px;
    }
  }

  .font-scale-slider {
    flex: 1;
    height: 6px;
    -webkit-appearance: none;
    appearance: none;
    background: #e0e0e0;
    border-radius: 3px;
    outline: none;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: var(--primary);
      cursor: pointer;
      transition: transform 0.2s ease;

      &:hover {
        transform: scale(1.1);
      }
    }

    &::-moz-range-thumb {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: var(--primary);
      cursor: pointer;
      border: none;
    }
  }

  .scale-value {
    min-width: 45px;
    font-weight: 600;
    color: var(--primary);
  }
}
</style>
